import { useMemo } from "react";
import { useSelector } from "react-redux";
import { can as canIn } from "@wine2trace/auth/permissions";
import {
  selectUserEffectivePermissions,
  selectUserGrantsAll,
  selectUserRbacPermissions,
  selectUserRoleCodes,
} from "../context/selectors";
import { ROLES } from "../constants/base";
import useAuth from "./useAuth";

const NONE = Object.freeze({});
const NO_ROLES = Object.freeze([]);

// Permissions of the user in the active winery, from GET /users/me/rbac.
//   can(resource, action) -> the `effective` map: explicit grants + implied reads, exactly what the
//                            server will allow. Falls back to the explicit map only when the server
//                            sent no effective map at all (older server).
//   canSee(resource)      -> the explicit read: menu visibility (an implied read opens no menu entry).
// The platform ADMIN has no tenant access (only via impersonation, where the token carries the
// target's role): for a direct ADMIN everything is false.
const usePermissions = () => {
  const { auth } = useAuth();
  const explicit = useSelector(selectUserRbacPermissions);
  const effectiveMap = useSelector(selectUserEffectivePermissions);
  const grantsAllStored = useSelector(selectUserGrantsAll);
  const roleCodesStored = useSelector(selectUserRoleCodes);
  const isAdmin = Number(auth?.role) === ROLES.ADMIN;

  return useMemo(() => {
    const permissions = isAdmin ? NONE : explicit || NONE;
    const effective = isAdmin ? NONE : effectiveMap || permissions;
    return {
      can: (resource, action) => canIn(effective, resource, action),
      canSee: (resource) => canIn(permissions, resource, "read"),
      grantsAll: isAdmin ? false : grantsAllStored === true,
      roleCodes: isAdmin ? NO_ROLES : roleCodesStored || NO_ROLES,
      permissions,
      effective,
    };
  }, [isAdmin, explicit, effectiveMap, grantsAllStored, roleCodesStored]);
};

export default usePermissions;
